import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import { heroMedia, company } from "../mock";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full flex items-end overflow-hidden"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${heroMedia.background})` }}
        aria-hidden="true"
      />
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128]/85 via-[#0A1128]/70 to-[#0A1128]/95" />
      {/* Subtle grain */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-40 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <motion.p
              custom={0}
              initial="hidden"
              animate="show"
              variants={fade}
              data-testid="hero-overline"
              className="text-xs md:text-sm uppercase tracking-[0.32em] text-[#CFA660] font-semibold mb-6"
            >
              Boutique Advisory
            </motion.p>
            <motion.h1
              custom={1}
              initial="hidden"
              animate="show"
              variants={fade}
              data-testid="hero-headline"
              className="font-['Playfair_Display'] text-white text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] tracking-tight font-medium"
            >
              Strategic consulting
              <br />
              for <span className="italic text-[#CFA660]">defining</span> moments.
            </motion.h1>
            <motion.p
              custom={2}
              initial="hidden"
              animate="show"
              variants={fade}
              data-testid="hero-sub"
              className="mt-8 max-w-xl text-white/80 text-lg md:text-xl font-light leading-relaxed font-['Outfit']"
            >
              Rampart Partners advises founders, owners, and executives on the
              decisions that shape the next decade — strategy, growth, and the
              transactions in between.
            </motion.p>

            <motion.div
              custom={3}
              initial="hidden"
              animate="show"
              variants={fade}
              className="mt-12 flex flex-col sm:flex-row gap-4"
            >
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("contact")}
                className="group inline-flex items-center justify-center gap-3 bg-white text-[#0A1128] px-8 py-5 text-sm font-semibold tracking-wide uppercase hover:bg-[#CFA660] hover:text-[#0A1128] transition-colors duration-300"
              >
                <Calendar className="w-4 h-4" />
                Schedule a Consultation
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("services")}
                className="inline-flex items-center justify-center gap-3 border border-white/40 text-white px-8 py-5 text-sm font-semibold tracking-wide uppercase hover:bg-white/10 transition-colors duration-300"
              >
                Explore Our Practice
              </button>
            </motion.div>
          </div>

          {/* Right side meta */}
          <motion.div
            custom={4}
            initial="hidden"
            animate="show"
            variants={fade}
            className="lg:col-span-4 lg:pl-8 border-l border-white/15"
          >
            <div className="pl-6 space-y-8">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#CFA660] mb-2">
                  Office
                </p>
                <p className="text-white/90 text-sm font-['Outfit'] leading-relaxed">
                  {company.address}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#CFA660] mb-2">
                  Direct
                </p>
                <p className="text-white text-sm font-['Outfit']">
                  {company.phone}
                </p>
                <p className="text-white/70 text-sm font-['Outfit'] mt-1">
                  {company.email}
                </p>
              </div>
              <div className="pt-4 border-t border-white/10">
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
                  By appointment only
                </p>
                <p className="text-white/80 text-xs font-['Outfit']">
                  All engagements begin with a private, no-obligation conversation.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="hidden md:flex absolute bottom-8 right-12 items-center gap-3 text-white/60 text-[10px] uppercase tracking-[0.3em]"
        >
          <span className="h-px w-12 bg-white/40" />
          Scroll
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
